<template>
  <v-container fluid>
    <!-- Chat Header -->
    <v-row align="center" class="chat-header mb-3">
      <v-avatar size="40">
        <v-img :src="friend.image || defaultImage" alt="User Image" />
      </v-avatar>
      <p class="chat-title">{{ friend.fullName }}</p>
    </v-row>

    <!-- Messages List -->
    <v-row class="messages-list" justify="start">
      <v-col cols="12">
        <div class="messages" ref="messagesContainer">
          <div
            v-for="message in filteredMessages"
            :key="message.id"
            :class="[
              'message-container',
              { 'from-current-user': message.senderId === currentUser.uid },
              { 'from-other-user': message.senderId !== currentUser.uid }
            ]"
          >
            <v-avatar
              v-if="message.senderId !== currentUser.uid"
              size="32"
              class="avatar-left"
            >
              <v-img :src="friend.image || defaultImage" />
            </v-avatar>

            <div
              :class="[
                'message-bubble',
                { 'current-user': message.senderId === currentUser.uid },
                { 'other-user': message.senderId !== currentUser.uid }
              ]"
            >
              <p>{{ message.text }}</p>
              <span class="message-timestamp">
                {{ formatTimestamp(message.timestamp) }}
              </span>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Message Input -->
    <v-row align="center" class="message-input mt-4">
      <v-text-field
        v-model="newMessage"
        placeholder="Type a message..."
        class="flex-grow"
        dense
        outlined
        hide-details
        @keyup.enter="sendMessage"
      />
      <v-btn color="primary" @click="sendMessage" :disabled="!newMessage.trim()">
        <v-icon>mdi-send</v-icon>
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { db, auth } from "@/firebase";
import { format } from "date-fns";

export default {
  data() {
    return {
      friend: {},
      messages: [],
      newMessage: "",
      currentUser: {},
      defaultImage: require("../assets/Valorrr.png"),
    };
  },
  computed: {
    filteredMessages() {
      return this.messages
        .filter(
          (msg) =>
            (msg.senderId === this.currentUser.uid && msg.receiverUid === this.friend.id) ||
            (msg.senderId === this.friend.id && msg.receiverUid === this.currentUser.uid)
        )
        .sort((a, b) => a.timestamp?.toMillis?.() - b.timestamp?.toMillis?.());
    },
  },
  methods: {
    async fetchFriend() {
      const friendId = this.$route.params.friendId;
      if (!friendId) return;

      const friendRef = doc(db, "user", friendId);
      const friendSnapshot = await getDoc(friendRef);
      if (friendSnapshot.exists()) {
        this.friend = { id: friendSnapshot.id, ...friendSnapshot.data() };
      }
    },
    listenForMessages() {
      const messagesRef = collection(db, "messages");
      const q = query(
        messagesRef,
        where("senderId", "in", [this.currentUser.uid, this.friend.id]),
        where("receiverUid", "in", [this.currentUser.uid, this.friend.id]),
        orderBy("timestamp", "asc")
      );

      onSnapshot(q, (snapshot) => {
        this.messages = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        this.$nextTick(() => {
          const container = this.$refs.messagesContainer;
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      });
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return;

      const message = {
        text: this.newMessage.trim(),
        senderId: this.currentUser.uid,
        senderImage: this.currentUser.photoURL || "", // optional
        senderName: this.currentUser.displayName || "",
        receiverUid: this.friend.id,
        timestamp: Timestamp.now(),
        received: false,
      };

      await addDoc(collection(db, "messages"), message);
      this.newMessage = "";
    },
    formatTimestamp(timestamp) {
      try {
        return format(timestamp.toDate(), "p, MMM d");
      } catch {
        return "";
      }
    },
    async markMessagesAsRead() {
      const q = query(
        collection(db, "messages"),
        where("senderId", "==", this.friend.id),
        where("receiverUid", "==", this.currentUser.uid),
        where("received", "==", false)
      );

      const snapshot = await getDocs(q);
      for (const docSnap of snapshot.docs) {
        await updateDoc(doc(db, "messages", docSnap.id), {
          received: true,
        });
      }
    },
  },
  async mounted() {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    this.currentUser = {
      uid: currentUser.uid,
      displayName: currentUser.displayName,
      photoURL: currentUser.photoURL,
    };

    await this.fetchFriend();
    if (this.friend.id) {
      this.listenForMessages();
      await this.markMessagesAsRead();
    }
  },
};
</script>


<style scoped>
..chat-header {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #ccc;
  background: linear-gradient(to right, maroon, #800000);
  color: white;
}

.chat-title {
  font-size: 1.25rem;
  font-weight: bold;
  margin-left: 16px;
}

.messages {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  height: 60vh;
  overflow-y: auto;
  background-color: white;
  border-radius: 8px;
}

.message-container {
  display: flex;
  align-items: flex-end;
}

.message-container.from-current-user {
  justify-content: flex-end;
}

.message-container.from-other-user {
  justify-content: flex-start;
}

.avatar-left {
  margin-right: 8px;
}

.message-bubble {
  max-width: 65%;
  padding: 12px;
  border-radius: 16px;
  font-size: 0.9rem;
  position: relative;
  word-wrap: break-word;
}

.message-bubble.current-user {
  background-color: #007bff;
  color: white;
}

.message-bubble.other-user {
  background-color: #f1f1f1;
  color: black;
}

.message-timestamp {
  font-size: 0.75rem;
  color: #666;
  margin-top: 4px;
  display: block;
  text-align: right;
}

.message-input {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 12px 16px;
  border-top: 1px solid #ccc;
}

.v-text-field {
  background-color: white;
  border-radius: 10px;
}

.v-btn {
  color: white;
  background-color: #4caf50;
  border-radius: 20px;
  margin-left: 10px;
}

.v-btn:hover {
  background-color: #45a049;
}

</style>

